import { Component, computed, DestroyRef, effect, HostListener, inject, OnDestroy, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { catchError, filter, Observable, Subscription, throwError } from 'rxjs';
import { ToastService } from '../../../ui';
import { LoggingService } from '../../../core/services/logging.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Invoice, InvoiceService } from '../invoice.service';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmIconComponent,
  HlmSpinnerComponent,
  HlmAlertComponent,
} from '../../../ui';

export interface DetailPdfDialogData {
  invoiceId: number;
  invoiceNumber: number;
  allInvoices: Invoice[];
  currentIndex: number;
}

type DetailAction = 'credit' | 'regenerate' | 'recreateOriginal' | 'recreateCurrent' | 'delete' | 'final';
type CreatingAction = 'credit' | 'regenerate' | 'final';

/** Usage-log action names — these actions change billing data (Delete / Turn to final are irreversible). */
const ACTION_LOG_NAME: Record<CreatingAction, string> = {
  credit: 'generate_credit',
  regenerate: 'regenerate_invoice',
  final: 'turn_to_final',
};

/**
 * Invoice detail PDF viewer + invoice actions.
 *
 * @ticket EB-4396 — behaviour copied from the `pinguin-invoice` web component old CMT
 * embeds (`bower_components/pinguin-invoice/invoice-element/main.js`):
 *  - Generate credit / Regenerate create a **new preliminary invoice**. The viewer
 *    switches to it, regenerates its PDF and the list behind the dialog is refreshed
 *    (`invoicesChanged`). The new invoice is not part of the list the dialog was opened
 *    with, so prev/next navigation is disabled from then on ("detached").
 *  - Both are hidden for credit notes (`!invoice.isCreditNote`).
 *  - Recreate PDF = `GET …/pdf?recreate=true&updateAddress=…`; the response is the PDF.
 *  - A **preliminary** invoice (`!invoiceNumber && isTestRun`) offers ONLY Delete
 *    (`DELETE /invoices/{id}`, then close + refresh) and Turn to final
 *    (`PUT …/associatenumber` — numbers it and sends it to print). Old CMT has no
 *    confirmation for either; AMIE confirms both (Style Guide §17, destructive/irreversible).
 *  - Titles, markers and button labels are the old texts verbatim (verified in the
 *    running old CMT on 2026-09-24, customer 13279).
 *  - A stored PDF that is missing (404) is recreated once on open.
 *  - While a PDF loads or an action runs, navigation and all actions are blocked (legacy
 *    hides the footer while `!loaded`), only one PDF request is in flight (a newer one
 *    cancels the older, so a late response can never replace the current PDF), and the
 *    dialog cannot be closed (so the list refresh always reaches the opener).
 *  - Arrow-key navigation only acts while this dialog is the top-most one — never
 *    underneath a confirmation (code review EB-4396 H1).
 */
@Component({
  selector: 'cmt-detail-pdf-dialog',
  imports: [
    ...HLM_DIALOG_SHELL,
    HlmBtnDirective,
    HlmIconComponent,
    HlmSpinnerComponent,
    HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="xl" class="!max-h-[92vh] !h-[92vh] !w-[90vw] !max-w-[90vw]">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucideFileText" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <!-- Invoice-family dialog title (same as add-billing / generate-invoice / add-credit):
               larger and in the display face so the heading is easy to read in a dialog this tall. -->
          <h2 hlmDialogTitle class="font-display text-xl">{{ title() }}</h2>
          <p hlmDialogDescription>
            @if (preliminary()) {
              <strong data-testid="preliminary-marker" class="font-semibold text-foreground">{{ preliminaryLabel() }}</strong>
            } @else {
              <span class="font-mono-feat">{{ current().showReId }}</span>
            }
            @if (detached()) {
              <span class="ml-1">— {{ detachedReason() }} just now, the invoice list behind this dialog has been refreshed</span>
            } @else {
              <span class="ml-1">— invoice {{ currentIndex() + 1 }} of {{ data.allInvoices.length }}</span>
              <span class="ml-1 text-muted-foreground">· use arrow keys to navigate</span>
            }
          </p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()" [disabled]="!!actionLoading()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody class="flex min-h-0 flex-1 flex-col gap-3">
        <!-- Navigation bar — disabled (not hidden) once detached, as old CMT nulls prev/next -->
        <div class="flex items-center justify-center gap-3">
          <button
            hlmBtn variant="ghost" size="icon"
            aria-label="Previous invoice"
            (click)="navigatePrevious()"
            [disabled]="detached() || currentIndex() <= 0 || busy()"
          >
            <hlm-icon name="lucideChevronLeft" size="1rem" />
          </button>
          <span class="font-mono-feat text-sm text-muted-foreground">
            @if (detached()) { – } @else { {{ currentIndex() + 1 }} / {{ data.allInvoices.length }} }
          </span>
          <button
            hlmBtn variant="ghost" size="icon"
            aria-label="Next invoice"
            (click)="navigateNext()"
            [disabled]="detached() || currentIndex() >= data.allInvoices.length - 1 || busy()"
          >
            <hlm-icon name="lucideChevronRight" size="1rem" />
          </button>
        </div>

        @if (actionError()) {
          <hlm-alert tone="error">
            <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
            <span class="flex-1">{{ actionError() }}</span>
            <button hlmBtn variant="ghost" size="icon" type="button" aria-label="Dismiss error" (click)="actionError.set(null)">
              <hlm-icon name="lucideX" size="1rem" />
            </button>
          </hlm-alert>
        }

        <!-- PDF viewer -->
        @if (loading()) {
          <div class="flex flex-1 items-center justify-center gap-3 text-sm text-muted-foreground">
            <hlm-spinner size="24" />
            Loading PDF…
          </div>
        } @else if (errorMsg()) {
          <hlm-alert tone="error">
            <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
            <span>{{ errorMsg() }}</span>
          </hlm-alert>
        } @else if (pdfUrl()) {
          <iframe
            [src]="pdfUrl()!"
            class="flex-1 rounded-lg border border-border"
            title="Invoice Detail PDF"
          ></iframe>
        }
      </section>

      <footer hlmDialogFooter class="flex-wrap gap-y-2">
        <!-- Action sets + labels copied verbatim from pinguin-invoice (views Np / Rp / Fp).
             The invoice actions are the purpose of this dialog, so they carry the primary
             weight (default; Delete = destructive) and Close steps back to outline.
             font-semibold is a deliberate local choice: these long labels sit next to the
             outline Close and must not be skimmed past. Not a new app-wide button rule. -->
        <div class="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          @if (preliminary()) {
            <button hlmBtn variant="destructive" size="sm" class="font-semibold" (click)="deleteInvoice()" [disabled]="busy()">
              @if (actionLoading() === 'delete') { <hlm-spinner size="14" /> }
              {{ deleteLabel() }}
            </button>
            <button hlmBtn variant="default" size="sm" class="font-semibold" (click)="turnToFinal()" [disabled]="busy()">
              @if (actionLoading() === 'final') { <hlm-spinner size="14" /> }
              {{ finalLabel() }}
            </button>
          } @else {
            @if (!current().isCreditNote) {
              <button hlmBtn variant="default" size="sm" class="font-semibold" (click)="generateCredit()" [disabled]="busy()">
                @if (actionLoading() === 'credit') { <hlm-spinner size="14" /> }
                Generate credit (Billing dep. only)
              </button>
              <button hlmBtn variant="default" size="sm" class="font-semibold" (click)="regenerateInvoice()" [disabled]="busy()">
                @if (actionLoading() === 'regenerate') { <hlm-spinner size="14" /> }
                Regenerate invoice (Billing dep. only)
              </button>
            }
            <button hlmBtn variant="default" size="sm" class="font-semibold" (click)="recreatePdf(false)" [disabled]="busy()">
              @if (actionLoading() === 'recreateOriginal') { <hlm-spinner size="14" /> }
              Recreate PDF with original address
            </button>
            <button hlmBtn variant="default" size="sm" class="font-semibold" (click)="recreatePdf(true)" [disabled]="busy()">
              @if (actionLoading() === 'recreateCurrent') { <hlm-spinner size="14" /> }
              Recreate PDF with current address
            </button>
          }
        </div>
        <button hlmBtn variant="outline" size="sm" (click)="dialogRef.close()" [disabled]="!!actionLoading()">Close</button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class DetailPdfDialogComponent implements OnInit, OnDestroy {
  readonly data = inject<DetailPdfDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<DetailPdfDialogComponent>);
  private readonly invoiceService = inject(InvoiceService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly toast = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loggingService = inject(LoggingService);

  /**
   * Emits after an action changed the customer's invoices (credit, regenerate, turn to
   * final, delete) — the opener reloads its list.
   */
  readonly invoicesChanged = output<void>();

  readonly loading = signal(true);
  readonly errorMsg = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly pdfUrl = signal<SafeResourceUrl | null>(null);
  readonly currentIndex = signal(0);
  readonly actionLoading = signal<DetailAction | null>(null);
  /** The invoice on screen — a list entry, or the new invoice an action created. */
  readonly current = signal<Invoice>(this.data.allInvoices[this.data.currentIndex] ?? this.fallbackInvoice());
  /** True once the viewer shows an invoice that is not part of `data.allInvoices`. */
  readonly detached = signal(false);
  /** What the last action did to the invoice on screen — drives the description line. */
  readonly detachedReason = signal<'created' | 'turned to final'>('created');
  /** A PDF is loading or an action is running — navigation and actions wait. */
  readonly busy = computed(() => this.loading() || this.actionLoading() !== null);

  /** Old CMT `isPreliminaryInvoice()`: no number yet and a test run. */
  readonly preliminary = computed(() => !this.current().showReId && this.current().isTestRun === true);
  readonly title = computed(() => (this.current().isCreditNote ? 'Detail of the credit note' : 'Detail of the invoice'));
  readonly preliminaryLabel = computed(() => (this.current().isCreditNote ? 'Preliminary credit' : 'Preliminary invoice'));
  readonly deleteLabel = computed(() => (this.current().isCreditNote ? 'Delete credit note' : 'Delete invoice'));
  readonly finalLabel = computed(() => (this.current().isCreditNote ? 'Turn to final credit note' : 'Turn to final invoice'));

  private blobUrl: string | null = null;
  /** The single in-flight PDF request; a newer load cancels it (H2). */
  private pdfRequest: Subscription | null = null;

  constructor() {
    // No close (backdrop / Escape) while an action runs — its result must reach the opener.
    effect(() => {
      this.dialogRef.disableClose = this.actionLoading() !== null;
    });
  }

  ngOnInit(): void {
    this.currentIndex.set(this.data.currentIndex);
    this.loadPdf(this.current().id);
  }

  ngOnDestroy(): void {
    this.pdfRequest?.unsubscribe();
    this.revokeBlobUrl();
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.busy() || this.detached()) return;
    // Never navigate underneath another dialog (e.g. the delete / turn-to-final confirm).
    const openDialogs = this.dialog.openDialogs;
    const topMost = openDialogs[openDialogs.length - 1];
    if (topMost && topMost !== this.dialogRef) return;
    if (event.key === 'ArrowLeft') this.navigatePrevious();
    if (event.key === 'ArrowRight') this.navigateNext();
  }

  navigatePrevious(): void {
    const newIndex = this.currentIndex() - 1;
    if (this.detached() || this.busy() || newIndex < 0) return;
    this.navigateTo(newIndex);
  }

  navigateNext(): void {
    const newIndex = this.currentIndex() + 1;
    if (this.detached() || this.busy() || newIndex >= this.data.allInvoices.length) return;
    this.navigateTo(newIndex);
  }

  generateCredit(): void {
    if (this.busy()) return;
    this.runCreatingAction(
      'credit',
      this.current().id,
      this.invoiceService.generateCredit(this.current().id),
      'Credit has been created.',
      'Problem occurred during creation of credit.',
    );
  }

  regenerateInvoice(): void {
    if (this.busy()) return;
    this.runCreatingAction(
      'regenerate',
      this.current().id,
      this.invoiceService.regenerateInvoice(this.current().id),
      'Invoice has been recreated.',
      'Problem occurred during recreation of invoice.',
    );
  }

  /** Delete a preliminary invoice / credit note — closes the dialog on success. */
  deleteInvoice(): void {
    if (this.busy()) return;
    const invoiceId = this.current().id; // fixed before the confirm (H1)
    const label = this.deleteLabel();
    const kind = this.current().isCreditNote ? 'credit note' : 'invoice';
    this.confirm({
      title: label,
      // KV RechnungLogik: "Sind Sie sicher, dass Sie diese Rechnung löschen wollen?"
      message: `Are you sure you want to delete this preliminary ${kind}? This cannot be undone.`,
      confirmLabel: label,
      confirmColor: 'warn',
    }).subscribe(() => {
      this.actionLoading.set('delete');
      this.actionError.set(null);
      this.invoiceService.deleteInvoice(invoiceId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.actionLoading.set(null);
          this.loggingService.usage('action.invoke', { action: 'delete_invoice', outcome: 'success', invoiceId });
          this.toast.open('Invoice has been deleted.', 'OK', { duration: 3000, tone: 'success' });
          this.invoicesChanged.emit();
          this.dialogRef.close();
        },
        error: (err: HttpErrorResponse) => {
          this.actionLoading.set(null);
          this.loggingService.usage('action.invoke', { action: 'delete_invoice', outcome: 'error', invoiceId, status: err?.status });
          this.reportActionError(err, 'Problem occurred during deletion of invoice.');
        },
      });
    });
  }

  /** Turn a preliminary invoice into a final one — numbered and sent to print. Irreversible. */
  turnToFinal(): void {
    if (this.busy()) return;
    const invoiceId = this.current().id; // fixed before the confirm (H1)
    const label = this.finalLabel();
    const kind = this.current().isCreditNote ? 'credit note' : 'invoice';
    this.confirm({
      title: label,
      // invoice-api `associatenumber`: "Turn a preliminary invoice into a final invoice. Then send it to be printed."
      message: `This assigns an invoice number to the ${kind} and sends it to be printed. This cannot be undone.`,
      confirmLabel: label,
    }).subscribe(() =>
      this.runCreatingAction(
        'final',
        invoiceId,
        this.invoiceService.turnToFinal(invoiceId),
        'Number has been associated to the invoice.',
        'Problem occurred during association of the number for invoice.',
      ),
    );
  }

  /** Emits once, only when the operator confirmed. */
  private confirm(data: ConfirmDialogData): Observable<boolean> {
    return this.dialog
      .open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, { data, panelClass: 'cmt-hlm-dialog' })
      .afterClosed()
      .pipe(
        filter((confirmed): confirmed is true => confirmed === true),
        takeUntilDestroyed(this.destroyRef),
      );
  }

  /** Recreate the PDF of the invoice on screen — with the original or the current billing address. */
  recreatePdf(updateAddress: boolean): void {
    if (this.busy()) return;
    this.actionLoading.set(updateAddress ? 'recreateCurrent' : 'recreateOriginal');
    this.actionError.set(null);
    this.loading.set(true);
    this.pdfRequest?.unsubscribe();
    this.pdfRequest = this.invoiceService
      .recreatePdf(this.current().id, updateAddress)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (blob) => {
        this.showPdf(blob);
        this.actionLoading.set(null);
        this.toast.open('PDF has been recreated and reloaded.', 'OK', { duration: 3000, tone: 'success' });
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        this.actionLoading.set(null);
        // legacy findWithRetryPdf error text, verbatim
        this.reportActionError(err, 'Problem with loading PDF for invoice.');
      },
    });
  }

  /** Credit / regenerate / turn to final: the server returns an invoice to switch to — show it. */
  private runCreatingAction(
    action: CreatingAction,
    sourceId: number,
    request: Observable<Invoice>,
    successMsg: string,
    fallbackError: string,
  ): void {
    if (this.busy()) return;
    this.actionLoading.set(action);
    this.actionError.set(null);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (created) => {
        this.loggingService.usage('action.invoke', {
          action: ACTION_LOG_NAME[action], outcome: 'success', invoiceId: sourceId, createdInvoiceId: created.id,
        });
        this.current.set(created);
        this.detached.set(true);
        this.detachedReason.set(action === 'final' ? 'turned to final' : 'created');
        this.actionLoading.set(null);
        this.invoicesChanged.emit();
        this.toast.open(successMsg, 'OK', { duration: 3000, tone: 'success' });
        // Legacy reloads with recreate=true after these actions: a new invoice has no stored
        // PDF yet, a finalized one needs its number in the PDF.
        this.loadPdf(created.id, true);
      },
      error: (err: HttpErrorResponse) => {
        this.actionLoading.set(null);
        this.loggingService.usage('action.invoke', {
          action: ACTION_LOG_NAME[action], outcome: 'error', invoiceId: sourceId, status: err?.status,
        });
        this.reportActionError(err, fallbackError);
      },
    });
  }

  private reportActionError(err: HttpErrorResponse, fallback: string): void {
    const message = (err?.error as { message?: string } | null)?.message || fallback;
    this.actionError.set(message);
    this.toast.open(message, 'OK', { duration: 5000, tone: 'error' });
  }

  private navigateTo(index: number): void {
    this.currentIndex.set(index);
    this.current.set(this.data.allInvoices[index]);
    this.actionError.set(null);
    this.loadPdf(this.current().id);
  }

  /** Load the stored PDF; a missing one (404) is recreated once, as old CMT does. */
  private loadPdf(invoiceId: number, recreate = false): void {
    this.loading.set(true);
    this.errorMsg.set(null);
    this.pdfRequest?.unsubscribe(); // a newer load always wins (H2)
    const request = recreate
      ? this.invoiceService.recreatePdf(invoiceId, false)
      : this.invoiceService.getDetailPdf(invoiceId).pipe(
          // a missing stored PDF is recreated once — inside the same subscription, so the
          // retry stays cancellable by the next load (review run 2, N1)
          catchError((err: HttpErrorResponse) =>
            err?.status === 404 ? this.invoiceService.recreatePdf(invoiceId, false) : throwError(() => err),
          ),
        );
    this.pdfRequest = request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (blob) => this.showPdf(blob),
      error: () => {
        this.errorMsg.set('Failed to load invoice PDF');
        this.loading.set(false);
      },
    });
  }

  private showPdf(blob: Blob): void {
    this.errorMsg.set(null); // e.g. Recreate PDF after a failed load (review run 2, N3)
    this.revokeBlobUrl();
    this.blobUrl = URL.createObjectURL(blob);
    // #navpanes=1 shows page sidebar in Chrome's PDF viewer
    this.pdfUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl + '#navpanes=1'));
    this.loading.set(false);
  }

  private revokeBlobUrl(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }

  /** Opened with an id that is not in the list (defensive — the opener always passes one). */
  private fallbackInvoice(): Invoice {
    return {
      id: this.data.invoiceId, customerId: '', showReId: this.data.invoiceNumber,
      basePrice: 0, unitPrice: 0,
      baseFeeStartDate: '', baseFeeEndDate: '', serviceFeeStartDate: '', serviceFeeEndDate: '',
      creationDate: '', printingDate: null, envelopingDate: null,
    };
  }
}
